import { useEffect, useState } from 'react';
import { X, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle, Ticket, Camera, Mail, Copy, Calendar, Edit3, Baby } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import WaiverStampIcon from './WaiverStampIcon';
import DatePicker from './DatePicker';

const API = process.env.REACT_APP_BACKEND_URL;

let globalSetOpen = null;
let pendingPrefill = null;
export function openWaiver(prefill) { pendingPrefill = prefill || null; if (globalSetOpen) globalSetOpen(true); }

function InviteByEmail({ isJa, reservationNumber, inviterName }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);
  const [msg, setMsg] = useState('');
  const send = async () => {
    setBusy(true); setMsg('');
    try {
      await axios.post(`${API}/api/waiver/invite`, { email: email.trim(), reservation_number: reservationNumber, inviter_name: inviterName, lang: isJa ? 'ja' : 'en' });
      setSent(s => [...s, email.trim()]); setEmail('');
    } catch (e) {
      const st = e?.response?.status;
      setMsg(isJa ? (st === 400 ? '有効なメールアドレスを入力してください' : st === 429 ? 'しばらくしてからもう一度お試しください' : '送信できませんでした')
        : (e?.response?.data?.detail || 'Could not send'));
    }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-neutral-700 bg-black/40 p-3 space-y-2" data-testid="waiver-invite">
      <p className="text-[11px] text-neutral-300">
        <Mail size={11} className="inline mr-1" />
        {isJa ? 'ご自身で署名したい方には、メールで同意書を送れます（同じ予約番号が入ります）。' : 'Want someone to sign for themselves? Email them their own waiver (it includes your reservation number).'}
      </p>
      <div className="flex gap-2">
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (email.trim()) send(); } }}
          data-testid="waiver-invite-email" placeholder={isJa ? 'メンバーのメールアドレス' : "Group member's email"}
          className="flex-1 min-w-0 bg-black border border-neutral-700 focus:border-orange-500 rounded-lg px-3 py-2 text-sm outline-none" />
        <button type="button" onClick={send} disabled={busy || !email.trim()} data-testid="waiver-invite-send"
          className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-black px-3 rounded-lg">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} {isJa ? '送信' : 'Send Waiver'}
        </button>
      </div>
      {msg && <p className="text-[11px] text-red-400" data-testid="waiver-invite-error">{msg}</p>}
      {sent.map(s => <p key={s} className="text-[11px] text-emerald-400" data-testid="waiver-invite-sent"><CheckCircle2 size={11} className="inline mr-1" />{isJa ? `${s} に送信しました` : `Sent to ${s}`}</p>)}
    </div>
  );
}

const newGuest = () => ({ name: '', date_of_birth: '', age: '', is_minor: false, guardian_email: '' });
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const nameMatches = (a, b) => (a || '').trim().toLowerCase().replace(/\s+/g, ' ') === (b || '').trim().toLowerCase().replace(/\s+/g, ' ');

const T = (isJa) => ({
  title: isJa ? '免責同意書 — オキ-エスケープ' : 'Escape Room Waiver',
  required: isJa ? '入場前に必須' : 'Required Prior to Entry',
  leaderResponsibility: isJa
    ? 'グループリーダー1名がグループ全員の責任を代表して負うか、各ゲストにリンクを送って個別に署名してもらうことができます。'
    : 'One group leader can take responsibility for the whole group — or forward the waiver link so each guest signs on their own.',
  forwardLink: isJa ? 'リンクをコピー' : 'Copy Link to Forward',
  forwardCopied: isJa ? 'コピーしました！' : 'Copied — paste it to your guests',
  loading: isJa ? '読み込み中…' : 'Loading…',
  order: isJa ? '予約番号' : 'Reservation Number',
  orderHint: isJa
    ? '予約確認メールに記載の番号を入力してください。分からない場合は「わからない」を押してください。'
    : "Type the reservation number from your booking confirmation. Tap \"I don't know\" if you're not sure — staff can look it up at check-in.",
  idk: isJa ? 'わからない' : "I don't know",
  participantInfo: isJa ? '参加者情報' : 'Participant Information',
  fullName: isJa ? '氏名 (グループリーダー)' : 'Full Name (Group Leader)',
  dob: isJa ? '生年月日' : 'Date of Birth',
  age: isJa ? '年齢' : 'Age',
  phone: isJa ? '電話番号' : 'Phone Number',
  email: isJa ? 'メールアドレス' : 'Email',
  ecName: isJa ? '緊急連絡先 氏名' : 'Emergency Contact Name',
  ecPhone: isJa ? '緊急連絡先 電話番号' : 'Emergency Contact Phone',
  additional: isJa ? 'あなたのグループ' : 'Your Group',
  addNote: isJa
    ? 'あなたが責任を持つメンバーの名前を入力してください。お子様は「子どもを追加」から。18歳未満はあなたが保護者として承認します。'
    : "Type the names of the people you're responsible for. Parents: use \"Add my child\". You approve anyone under 18 as their guardian.",
  addGuestBtn: isJa ? 'メンバーを追加' : 'Add group member',
  addChildBtn: isJa ? '子どもを追加' : 'Add my child',
  guestName: isJa ? "ゲストの氏名" : "Guest's full name",
  minorLabel: isJa ? '18歳未満（グループリーダーが代理承認）' : 'Under 18 — leader approves on their behalf',
  photoTitle: isJa ? '写真・動画' : 'Photography & Video',
  photoIn: isJa ? 'この同意書に署名することで、当日撮影された写真・動画をOki-Escapeがプロモーション/SNSで使用することに同意したものとみなされます。' : 'By signing this waiver, you consent to Oki-Escape using photos and videos taken during your visit for promotion and social media.',
  photoAsk: isJa ? '写真撮影や使用を希望されない場合は、チェックイン時にスタッフへ直接お知らせください。' : "Don't want photos taken or used? Just let our staff know at check-in.",
  guardianTitle: isJa ? '保護者・法定代理人の同意（グループに18歳未満がいる場合）' : 'Parent / Legal Guardian Consent (only if group has minors)',
  guardianNote: isJa
    ? '未成年者と一緒に来場する保護者の情報を記入してください。あなたがそのまま保護者の場合はリーダー氏名と同じで構いません。'
    : "Fill in the details of the minor's parent or legal guardian. If YOU are the guardian, you may reuse the leader's name.",
  guardianName: isJa ? '保護者 氏名' : 'Parent/Guardian Full Name',
  guardianRel: isJa ? '続柄' : 'Relationship',
  guardianPhone: isJa ? '保護者 電話番号' : 'Parent/Guardian Phone',
  guardianSig: isJa ? '保護者 署名（氏名を再入力）' : 'Parent/Guardian Signature (retype name)',
  acknowledgeTitle: isJa ? '確認事項' : 'Acknowledgment',
  acceptAll: isJa
    ? '上記の警告・危険引受・ルール・免責をすべて読み、理解し、グループ全員を代表して自発的に同意します。'
    : 'I have read and understood the health warning, assumption of risk, rules, and release of liability — and voluntarily agree on behalf of every guest in my group.',
  ackRules: isJa
    ? '参加者ルールに同意し、グループ全員にルールを共有します。'
    : 'I agree to the participant rules and will share them with every guest in my group.',
  signatureTitle: isJa ? 'グループリーダー 署名' : 'Group Leader Signature',
  signaturePlaceholder: isJa ? '氏名を再入力（署名として）' : 'Retype your name as legal signature',
  signatureMismatch: isJa
    ? '署名は上記の氏名と完全に一致する必要があります'
    : 'Signature must exactly match the participant name above',
  todayLabel: isJa ? '日付' : 'Date',
  editBtn: isJa ? '編集' : 'Edit',
  confirmBtn: isJa ? '確認' : 'Confirm',
  submitBtn: isJa ? '同意して送信' : 'Sign & Submit',
  signing: isJa ? '送信中…' : 'Signing…',
  disabledMsg: isJa ? '免責同意書は現在ご利用いただけません' : 'Waivers are currently disabled.',
  successTitle: isJa ? '同意書を受け付けました' : 'Waiver Signed',
  close: isJa ? '閉じる' : 'Close',
  awaitingGuardian: isJa ? '保護者の承認待ち' : 'Awaiting Guardian Approval',
  guardianEmailedNote: isJa
    ? '保護者にメールが送信されました。保護者がリンクをクリックして承認するまで同意書は有効になりません。'
    : "We've emailed the guardian(s). Your waiver becomes active once each guardian clicks the link and approves.",
  requiredAsterisk: '*',
});

export default function WaiverModal() {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');

  const [reservationNumber, setReservationNumber] = useState('');
  const [leaderName, setLeaderName] = useState('');
  const [leaderDob, setLeaderDob] = useState('');
  const [leaderAge, setLeaderAge] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ecName, setEcName] = useState('');
  const [ecPhone, setEcPhone] = useState('');
  const [additional, setAdditional] = useState([]);
  const [guardianName, setGuardianName] = useState('');
  const [guardianRel, setGuardianRel] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [guardianSig, setGuardianSig] = useState('');
  const [rulesOk, setRulesOk] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [signedAt, setSignedAt] = useState(todayStr());
  const [editingDate, setEditingDate] = useState(false);
  const [leaderIsMinor, setLeaderIsMinor] = useState(false);
  const [leaderGuardianEmail, setLeaderGuardianEmail] = useState('');

  const { lang } = useLanguage();
  const isJa = lang === 'ja';
  const t = T(isJa);

  useEffect(() => { globalSetOpen = setOpen; return () => { globalSetOpen = null; }; }, []);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/waiver/config`).then(r => setCfg(r.data)).catch(() => {});
    setDone(null); setErr(''); setAccepted(false); setRulesOk(false);
    setReservationNumber(pendingPrefill?.reservation_number || ''); pendingPrefill = null; setLeaderName(''); setLeaderDob(''); setLeaderAge('');
    setEmail(''); setPhone(''); setEcName(''); setEcPhone('');
    setAdditional([]);
    setGuardianName(''); setGuardianRel(''); setGuardianPhone(''); setGuardianSig('');
    setSignature(''); setLeaderIsMinor(false); setLeaderGuardianEmail('');
    setSignedAt(todayStr()); setEditingDate(false);
  }, [open]);

  if (!open) return null;
  if (cfg && !cfg.enabled) {
    return (
      <div className="fixed inset-0 z-[9998] bg-black/80 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
        <div className="bg-card border border-border rounded-2xl max-w-md p-6" onClick={e => e.stopPropagation()}>
          <p className="text-center">{t.disabledMsg}</p>
        </div>
      </div>
    );
  }

  const legalText = cfg ? (isJa ? cfg.legal_text_ja : cfg.legal_text_en) : '';
  const hasMinor = leaderIsMinor || additional.some(g => g.is_minor);
  const minors = additional.filter(g => g.is_minor);
  const parentIsLeader = !leaderIsMinor && minors.length > 0 && minors.every(g => g.is_child);
  const needGuardianBlock = hasMinor && !leaderIsMinor && !parentIsLeader;
  const needsLeaderGuardianEmail = leaderIsMinor;

  const addGuest = () => setAdditional(g => [...g, newGuest()]);
  const addChild = () => setAdditional(g => [...g, { ...newGuest(), is_minor: true, is_child: true }]);
  const removeGuest = (i) => setAdditional(g => g.filter((_, idx) => idx !== i));
  const updateGuest = (i, patch) => setAdditional(g => g.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const canSubmit = () => {
    if (!accepted || !rulesOk) return false;
    if (!reservationNumber.trim()) return false;
    if (!leaderName.trim() || !signature.trim()) return false;
    if (!nameMatches(signature, leaderName)) return false;
    if (!email || !email.includes('@')) return false;
    if (!phone.trim()) return false;
    for (const g of additional) if (!g.name.trim()) return false;
    if (needsLeaderGuardianEmail && (!leaderGuardianEmail || !leaderGuardianEmail.includes('@'))) return false;
    if (needGuardianBlock) {
      if (!guardianName.trim() || !guardianRel.trim() || !guardianPhone.trim() || !guardianSig.trim()) return false;
      if (!nameMatches(guardianSig, guardianName)) return false;
    }
    return true;
  };

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      // guest[0] is always the leader (name + optional minor flag)
      const guests = [
        {
          name: leaderName.trim(),
          is_minor: leaderIsMinor,
          guardian_email: leaderIsMinor ? leaderGuardianEmail.trim() : undefined,
          date_of_birth: leaderDob || undefined,
          age: leaderAge ? Number(leaderAge) : undefined,
        },
        ...additional.map(g => ({
          name: g.name.trim(),
          is_minor: !!g.is_minor,
          guardian_email: (g.is_minor && leaderIsMinor) ? (g.guardian_email || '').trim() : undefined,
          date_of_birth: g.date_of_birth || undefined,
          age: g.age ? Number(g.age) : undefined,
        })),
      ];
      const body = {
        email: email.trim(),
        phone: phone.trim(),
        reservation_number: reservationNumber.trim(),
        signature_typed: signature.trim(),
        accepted: true,
        rules_acknowledged: rulesOk,
        emergency_contact_name: ecName.trim() || undefined,
        emergency_contact_phone: ecPhone.trim() || undefined,
        photo_consent: true,
        photo_ask: false,
        guests,
        guardian_full_name: parentIsLeader ? leaderName.trim() : needGuardianBlock ? guardianName.trim() : undefined,
        guardian_relationship: parentIsLeader ? 'Parent' : needGuardianBlock ? guardianRel.trim() : undefined,
        guardian_phone: parentIsLeader ? (phone.trim() || ecPhone.trim()) : needGuardianBlock ? guardianPhone.trim() : undefined,
        guardian_signature: parentIsLeader ? signature.trim() : needGuardianBlock ? guardianSig.trim() : undefined,
        signed_at_local: signedAt,
      };
      const r = await axios.post(`${API}/api/waiver/submit`, body);
      setDone(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/85 flex items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={() => setOpen(false)}>
      <div onClick={e => e.stopPropagation()}
        className="bg-neutral-950 border-2 border-orange-500/50 rounded-2xl w-full max-w-2xl my-auto overflow-hidden flex flex-col"
        style={{ boxShadow: '0 0 40px rgba(255,120,20,0.35)', maxHeight: 'calc(100vh - 24px)' }}
        data-testid="waiver-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-700 via-orange-700 to-red-800 p-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <WaiverStampIcon size={26} />
            <h3 className="font-heading text-lg sm:text-xl tracking-widest uppercase text-white"
              style={{ textShadow: '0 0 10px rgba(0,0,0,0.6)' }}>
              {t.title}
            </h3>
          </div>
          <button onClick={() => setOpen(false)} data-testid="waiver-close"
            className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60">
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center space-y-4">
            <CheckCircle2 size={48} className="text-emerald-400 mx-auto" />
            <h4 className="font-heading text-lg tracking-widest uppercase">
              {done.status === 'pending_guardian' ? t.awaitingGuardian : t.successTitle}
            </h4>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">{done.message}</p>
            <div className="rounded-lg border border-border bg-black/40 p-3 text-xs font-mono inline-block">
              <div>ID: {done.waiver_id}</div>
              <div>{done.guest_count} guest{done.guest_count === 1 ? '' : 's'}{done.minor_count > 0 ? ` · ${done.minor_count} minor${done.minor_count === 1 ? '' : 's'}` : ''}</div>
            </div>
            {done.status === 'pending_guardian' && (
              <div className="rounded-lg border border-yellow-500/40 bg-yellow-950/30 px-3 py-2 text-xs text-yellow-200 max-w-md mx-auto flex items-start gap-2">
                <Mail size={14} className="mt-0.5 shrink-0" />
                <span>{t.guardianEmailedNote}</span>
              </div>
            )}
            <button onClick={() => setOpen(false)} data-testid="waiver-success-close"
              className="mt-2 bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-xs px-6 py-2.5 rounded-full">
              {t.close}
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-5 overflow-y-auto">
            {/* Required-prior banner */}
            <div className="rounded-lg border-2 border-orange-500/60 bg-orange-950/40 p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-orange-400 mt-0.5 shrink-0" />
              <div className="text-xs flex-1">
                <p className="font-heading text-orange-300 tracking-widest uppercase">{t.required}</p>
                <p className="text-orange-200/80 mt-1">{t.leaderResponsibility}</p>
                <ForwardLinkButton
                  label={t.forwardLink}
                  copiedLabel={t.forwardCopied}
                  href={typeof window !== 'undefined' ? `${window.location.origin}/?waiver=1` : ''}
                  color="orange"
                />
              </div>
            </div>

            {/* Reservation Number */}
            <Section title={t.order} icon={<Ticket size={13} />}>
              <div className="flex items-center gap-2">
                <input value={reservationNumber} onChange={e => setReservationNumber(e.target.value)}
                  data-testid="waiver-reservation-number"
                  placeholder={isJa ? '例: BK-XXXXX' : 'e.g. BK-XXXXX'}
                  className="flex-1 bg-black border border-orange-500/60 focus:border-orange-400 rounded-lg px-3 py-2 text-sm outline-none font-mono" />
                <button type="button" onClick={() => setReservationNumber('idk')}
                  data-testid="waiver-reservation-idk"
                  className="shrink-0 font-heading text-[10px] tracking-widest uppercase bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 text-neutral-200 px-3 py-2 rounded-lg">
                  {t.idk}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground">{t.orderHint}</p>
            </Section>

            {/* Participant Info */}
            <Section title={t.participantInfo}>
              <TextField label={t.fullName + ' ' + t.requiredAsterisk} value={leaderName} onChange={setLeaderName}
                testId="waiver-leader-name" placeholder={isJa ? '氏名' : 'Your full name'} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground block mb-0.5">{t.dob}</label>
                  <DatePicker
                    value={leaderDob}
                    onChange={setLeaderDob}
                    placeholder={isJa ? '日付を選択' : 'Pick a date'}
                    testId="waiver-leader-dob"
                    theme="orange"
                    dobMode
                    locale={isJa ? 'ja' : 'en'}
                  />
                </div>
                <TextField label={t.age} type="number" value={leaderAge} onChange={setLeaderAge} testId="waiver-leader-age" />
              </div>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={leaderIsMinor} onChange={e => setLeaderIsMinor(e.target.checked)}
                  data-testid="waiver-leader-minor"
                  className="w-4 h-4 accent-orange-500" />
                <span>{isJa ? 'リーダーが18歳未満（保護者の承認が必要）' : "I (the leader) am under 18 — guardian approval required"}</span>
              </label>
              {leaderIsMinor && (
                <TextField label={(isJa ? '保護者のメールアドレス' : "Parent/guardian's email") + ' ' + t.requiredAsterisk}
                  type="email" value={leaderGuardianEmail} onChange={setLeaderGuardianEmail}
                  testId="waiver-leader-guardian-email" placeholder="guardian@example.com" />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <TextField label={t.phone + ' ' + t.requiredAsterisk} type="tel" value={phone} onChange={setPhone} testId="waiver-phone" />
                <TextField label={t.email + ' ' + t.requiredAsterisk} type="email" value={email} onChange={setEmail}
                  testId="waiver-email" placeholder="you@example.com" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <TextField label={t.ecName} value={ecName} onChange={setEcName} testId="waiver-ec-name" />
                <TextField label={t.ecPhone} type="tel" value={ecPhone} onChange={setEcPhone} testId="waiver-ec-phone" />
              </div>
            </Section>

            {/* Legal text */}
            <div className="rounded-lg border border-orange-500/30 bg-black/60 p-3 text-[11px] leading-relaxed text-orange-100/85 max-h-56 overflow-y-auto whitespace-pre-line font-sans"
              data-testid="waiver-legal-text">
              {legalText || t.loading}
            </div>

            {/* Additional Guests */}
            <Section title={t.additional}>
              <div className="space-y-2">
                <p className="text-[10px] text-muted-foreground">{t.addNote}</p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={addGuest} data-testid="waiver-add-guest"
                    className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase bg-orange-600 hover:bg-orange-500 text-black px-3 py-1.5 rounded-full">
                    <Plus size={11} /> {t.addGuestBtn}
                  </button>
                  <button type="button" onClick={addChild} data-testid="waiver-add-child"
                    className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase border border-yellow-500/60 text-yellow-300 hover:bg-yellow-900/30 px-3 py-1.5 rounded-full">
                    <Baby size={12} /> {t.addChildBtn}
                  </button>
                </div>
              </div>
              {additional.length > 0 && (
                <div className="space-y-2">
                  {additional.map((g, i) => (
                    <div key={i} className="rounded-lg border border-neutral-700 bg-black/40 p-3 space-y-2" data-testid={`waiver-guest-${i}`}>
                      <div className="flex items-center gap-2">
                        <input value={g.name} onChange={e => updateGuest(i, { name: e.target.value })}
                          data-testid={`waiver-guest-name-${i}`}
                          placeholder={g.is_child ? (isJa ? 'お子様の氏名' : "Child's full name") : t.guestName}
                          className="flex-1 bg-black border border-neutral-700 focus:border-orange-500 rounded-lg px-3 py-2 text-sm outline-none" />
                        <button type="button" onClick={() => removeGuest(i)} data-testid={`waiver-remove-guest-${i}`}
                          className="w-9 h-9 rounded-lg border border-neutral-700 hover:border-red-500 hover:text-red-400 text-neutral-500 flex items-center justify-center">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <DatePicker
                          value={g.date_of_birth}
                          onChange={(v) => updateGuest(i, { date_of_birth: v })}
                          placeholder={isJa ? '生年月日' : 'Date of Birth'}
                          testId={`waiver-guest-dob-${i}`}
                          theme="orange"
                          dobMode
                          locale={isJa ? 'ja' : 'en'}
                        />
                        <input type="number" value={g.age} onChange={e => updateGuest(i, { age: e.target.value })}
                          data-testid={`waiver-guest-age-${i}`}
                          placeholder={t.age}
                          className="bg-black border border-neutral-700 focus:border-orange-500 rounded-lg px-2 py-1.5 text-xs outline-none" />
                      </div>
                      <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                        <input type="checkbox" checked={g.is_minor} onChange={e => updateGuest(i, { is_minor: e.target.checked })}
                          data-testid={`waiver-minor-${i}`}
                          className="w-4 h-4 accent-orange-500" />
                        <span>{t.minorLabel}</span>
                      </label>
                      {g.is_minor && leaderIsMinor && (
                        <input type="email" value={g.guardian_email} onChange={e => updateGuest(i, { guardian_email: e.target.value })}
                          data-testid={`waiver-guardian-email-${i}`}
                          placeholder={isJa ? '保護者のメールアドレス' : "Parent/guardian's email"}
                          className="w-full bg-black border border-yellow-500/60 focus:border-yellow-400 rounded-lg px-3 py-2 text-sm outline-none" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <InviteByEmail isJa={isJa} reservationNumber={reservationNumber} inviterName={leaderName} />
            </Section>

            {/* Photo consent */}
            <Section title={t.photoTitle} icon={<Camera size={13} />}>
              <div className="space-y-1.5 text-xs font-sans" data-testid="waiver-photo-notice">
                <p className="text-neutral-200">{t.photoIn}</p>
                <p className="text-neutral-400">{t.photoAsk}</p>
              </div>
            </Section>

            {/* Guardian block — only when a minor is in the group AND leader is adult */}
            {needGuardianBlock && (
              <Section title={t.guardianTitle}>
                <p className="text-[10px] text-muted-foreground">{t.guardianNote}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <TextField label={t.guardianName + ' ' + t.requiredAsterisk} value={guardianName} onChange={setGuardianName} testId="waiver-guardian-name" />
                  <TextField label={t.guardianRel + ' ' + t.requiredAsterisk} value={guardianRel} onChange={setGuardianRel} testId="waiver-guardian-rel"
                    placeholder={isJa ? '例: 母、父、叔父' : 'e.g. Mother, Father, Uncle'} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <TextField label={t.guardianPhone + ' ' + t.requiredAsterisk} type="tel" value={guardianPhone} onChange={setGuardianPhone} testId="waiver-guardian-phone" />
                  <TextField label={t.guardianSig + ' ' + t.requiredAsterisk} value={guardianSig} onChange={setGuardianSig} testId="waiver-guardian-sig"
                    placeholder={isJa ? '氏名を再入力' : 'Retype full name'} />
                </div>
                {guardianSig && guardianName && !nameMatches(guardianSig, guardianName) && (
                  <p className="text-[10px] text-red-400" data-testid="waiver-guardian-sig-mismatch">
                    <AlertTriangle size={10} className="inline mr-1" />{t.signatureMismatch}
                  </p>
                )}
              </Section>
            )}

            {/* Acknowledgment */}
            <Section title={t.acknowledgeTitle}>
              <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={rulesOk} onChange={e => setRulesOk(e.target.checked)}
                  data-testid="waiver-rules-ok" className="mt-0.5 w-4 h-4 accent-orange-500" />
                <span>{t.ackRules}</span>
              </label>
              <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)}
                  data-testid="waiver-accept" className="mt-0.5 w-4 h-4 accent-orange-500" />
                <span>{t.acceptAll}</span>
              </label>
            </Section>

            {/* Signature + today's date */}
            <Section title={t.signatureTitle}>
              <input value={signature} onChange={e => setSignature(e.target.value)}
                data-testid="waiver-signature"
                placeholder={t.signaturePlaceholder}
                className={`w-full bg-black border-2 rounded-lg px-3 py-3 text-base outline-none font-mono ${
                  signature && !nameMatches(signature, leaderName)
                    ? 'border-red-500 focus:border-red-400'
                    : 'border-orange-500/60 focus:border-orange-400'
                }`} />
              {signature && !nameMatches(signature, leaderName) && (
                <p className="text-[10px] text-red-400" data-testid="waiver-sig-mismatch">
                  <AlertTriangle size={10} className="inline mr-1" />{t.signatureMismatch}
                </p>
              )}
              <DateConfirm
                label={t.todayLabel}
                value={signedAt}
                editing={editingDate}
                onEdit={() => setEditingDate(true)}
                onChange={(v) => setSignedAt(v)}
                onConfirm={() => setEditingDate(false)}
                editLabel={t.editBtn}
                confirmLabel={t.confirmBtn}
                testId="waiver-date"
                color="orange"
              />
            </Section>

            {err && <p className="text-xs text-red-400 text-center" data-testid="waiver-error">{err}</p>}

            <button onClick={submit} disabled={!canSubmit() || busy} data-testid="waiver-submit"
              className="w-full bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-sm py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <WaiverStampIcon size={22} />}
              {busy ? t.signing : t.submitBtn}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Small floating button — sits just to the right of the weekly puzzle FAB (bottom-left area) */
export function WaiverFloatingButton() {
  const { lang } = useLanguage();
  const isJa = lang === 'ja';
  return (
    <button
      onClick={() => openWaiver()}
      data-testid="waiver-fab"
      title={isJa ? '免責同意書' : 'Sign Waiver'}
      aria-label="Sign Waiver"
      className="fixed bottom-14 left-24 sm:left-28 z-[9999] w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-110 group"
      style={{
        background: 'radial-gradient(circle at 30% 25%, #fff 0%, #f5e6c8 55%, #d4b076 100%)',
        boxShadow: '0 0 28px rgba(220,38,38,0.8), 0 4px 14px rgba(0,0,0,0.5)',
        border: '3px solid #dc2626',
      }}
    >
      <span className="pointer-events-none absolute inset-0 rounded-full border-2 border-red-500 animate-ping opacity-60" />
      <WaiverStampIcon size={42} />
      <span
        data-testid="waiver-fab-label"
        className="pointer-events-none absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap font-heading text-[10px] tracking-widest uppercase bg-red-600 text-white px-2 py-0.5 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.7)]">
        {isJa ? '同意書' : 'Waiver'}
      </span>
    </button>
  );
}

/* --- helpers --- */
function Section({ title, icon, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 font-heading text-[10px] tracking-widest uppercase text-muted-foreground border-b border-border pb-1">
        {icon}
        <span>{title}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, type = 'text', testId, placeholder }) {
  return (
    <div>
      <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground block mb-0.5">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        data-testid={testId} placeholder={placeholder}
        className="w-full bg-black border border-neutral-700 focus:border-orange-500 rounded-lg px-3 py-2 text-sm outline-none" />
    </div>
  );
}


/** Auto-filled today's date with a one-tap Edit / Confirm toggle */
function DateConfirm({ label, value, editing, onEdit, onChange, onConfirm, editLabel, confirmLabel, testId, color = 'orange' }) {
  const isRed = color === 'red';
  const iconCls = isRed ? 'text-red-300' : 'text-orange-300';
  const inputFocus = isRed ? 'focus:border-red-500' : 'focus:border-orange-500';
  const confirmBtnCls = isRed
    ? 'bg-red-600 hover:bg-red-500 text-white'
    : 'bg-orange-600 hover:bg-orange-500 text-black';
  return (
    <div className="rounded-lg border border-neutral-700 bg-black/40 p-3 flex items-center gap-2">
      <Calendar size={14} className={iconCls} />
      <span className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">{label}</span>
      {editing ? (
        <>
          <input type="date" value={value} onChange={e => onChange(e.target.value)}
            data-testid={`${testId}-input`}
            className={`flex-1 bg-black border border-neutral-700 ${inputFocus} rounded-md px-2 py-1 text-sm outline-none font-mono`} />
          <button type="button" onClick={onConfirm} data-testid={`${testId}-confirm`}
            className={`inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase ${confirmBtnCls} px-2.5 py-1 rounded-full`}>
            <CheckCircle2 size={11} /> {confirmLabel}
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 font-mono text-sm text-white" data-testid={`${testId}-value`}>{value}</span>
          <button type="button" onClick={onEdit} data-testid={`${testId}-edit`}
            className="inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 text-neutral-200 px-2.5 py-1 rounded-full">
            <Edit3 size={11} /> {editLabel}
          </button>
        </>
      )}
    </div>
  );
}

/** Copies a waiver deep-link so the leader can text/share it with guests who prefer to sign themselves. */
function ForwardLinkButton({ label, copiedLabel, href, color = 'orange' }) {
  const [copied, setCopied] = useState(false);
  if (!href) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(href);
    } catch {
      window.open(href, '_blank', 'noopener');
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };
  const cls = color === 'red'
    ? 'bg-red-900/60 border-red-500/60 text-red-200 hover:bg-red-800/70'
    : 'bg-orange-900/60 border-orange-500/60 text-orange-200 hover:bg-orange-800/70';
  return (
    <button type="button" onClick={onCopy} data-testid="waiver-forward-link"
      className={`mt-2 inline-flex items-center gap-1.5 font-heading text-[10px] tracking-widest uppercase border px-2.5 py-1 rounded-full transition-colors ${cls}`}>
      {copied ? <CheckCircle2 size={11} /> : <Copy size={11} />}
      {copied ? copiedLabel : label}
    </button>
  );
}
